import { useEffect, useState } from 'react';
import type { MicrositeDevice, MicrositeSection, SectionVisibility } from '@/services/microsite';

/**
 * `value`, but only after it has stopped changing for `delay` ms.
 *
 * The live preview is a server render, so keying it directly off the draft
 * would fire a request per keystroke. Debouncing the *input* rather than
 * throttling the request keeps the render count proportional to edits the
 * author has finished making.
 */
export function useDebouncedValue<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** "cta_label" -> "Cta Label". */
export function humanize(field: string): string {
  return field.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * A fresh section id.
 *
 * Ids must be unique within a page — `sections.validate_sections` rejects a
 * duplicate and the whole save fails — which is exactly why duplicating a
 * section has to mint a new one rather than copying the envelope wholesale.
 */
export function newSectionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `s_${crypto.randomUUID()}`;
  }
  return `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Deep-copy a section config.
 *
 * A JSON round trip rather than `structuredClone`: the config *is* JSON (it is
 * stored in a JSONField and validated as such), and a shallow copy would leave
 * the duplicate sharing nested arrays like `items` with its original, so editing
 * one FAQ entry would silently edit both.
 */
export function cloneConfig(config: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!config) return {};
  try {
    return JSON.parse(JSON.stringify(config)) as Record<string, unknown>;
  } catch {
    return { ...config };
  }
}

/** A duplicate of `section`, ready to insert: same content, brand new id. */
export function duplicateSection(section: MicrositeSection): MicrositeSection {
  return {
    ...section,
    id: newSectionId(),
    config: cloneConfig(section.config),
    visibility: section.visibility ? { ...section.visibility } : undefined,
  };
}

/** Is this section shown on `device`? Absent/`true` both mean shown. */
export function isShownOn(section: MicrositeSection, device: MicrositeDevice): boolean {
  return section.visibility?.[device] !== false;
}

/**
 * Flip one breakpoint. Turning a device back ON deletes the key rather than
 * setting `true`, so a section the author never restricted stays a bare
 * envelope — the stored JSON reads as what the author actually decided.
 */
export function toggleDeviceVisibility(
  section: MicrositeSection,
  device: MicrositeDevice,
): MicrositeSection {
  const next: SectionVisibility = { ...(section.visibility ?? {}) };
  if (isShownOn(section, device)) {
    next[device] = false;
  } else {
    delete next[device];
  }
  return {
    ...section,
    visibility: Object.keys(next).length > 0 ? next : undefined,
  };
}

/** A short human summary of a section's visibility, for the card header. */
export function visibilitySummary(section: MicrositeSection, devices: MicrositeDevice[]): string {
  if (section.hidden) return 'Hidden everywhere';
  const off = devices.filter((d) => !isShownOn(section, d));
  if (off.length === 0) return 'Visible on all devices';
  if (off.length === devices.length) return 'Hidden on every breakpoint';
  return `Hidden on ${off.join(', ')}`;
}

/** Renumber `order` from array position — the ordering is canonical on save. */
export function renumber(sections: MicrositeSection[]): MicrositeSection[] {
  return sections.map((s, i) => ({ ...s, order: i }));
}
